/**
 * Pure helpers for the native lab-ordering flow.
 *
 * These were extracted from the original wizard dialog so the new full-screen
 * order flow (and its cart hook) can share a single, unit-tested source of
 * truth for lab anchoring, bucket auto-sorting, AOE aggregation, dedup, and
 * draft-payload construction. No React / data-fetching here — keep it pure.
 */
import type { InsuranceCoverage } from "@/types/insurance-coverage";
import type {
  AccessCollectionMethod,
  AoEAnswer,
  AoEAnswerOption,
  AoEQuestion,
  CartBucket,
  FastingStatus,
  JunctionPayor,
  LabAccount,
  LabBillingType,
  LabCollectionMethod,
  LabInfo,
  LabMedicalProxy,
  LabOrderCreate,
  LabOrderCreateItem,
  LabTest,
  LabTestStatus,
  Marker,
} from "@/types/lab";
import type { ICDTenCode } from "@/types/medical-codes";

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

/**
 * How tightly the current cart is bound to a single lab:
 *  - `agnostic`: nothing selected yet, any lab is fair game
 *  - `soft`: markers span multiple labs (resolved by auto-sort into buckets)
 *  - `hard`: a preset, or all markers from one lab, locks the primary order
 */
export type AnchorState = "agnostic" | "soft" | "hard";

/**
 * Product gate for cross-lab (multi-order) ordering. The backend already places
 * one order per lab regardless, so this is purely a frontend UX decision — the
 * only place the flag is read. While off, the flow stays single-lab (cross-lab
 * selections are rejected); flip to `true` + deploy to enable it for everyone.
 * The cart is bucketed by lab either way, so enabling it needs no other change.
 */
export const MULTI_LAB_ENABLED = false;

/**
 * Display label for every collection method the enum can hold, retired ones
 * included — an order placed as a Test Kit still has to render as "Test Kit".
 * Keyed in display order, which is the order filters offer them in.
 */
export const COLLECTION_METHOD_LABELS: Record<LabCollectionMethod, string> = {
  walk_in_test: "Walk-in",
  at_home_phlebotomy: "At-Home Phlebotomy",
  on_site_collection: "On-Site Collection",
  testkit: "Test Kit",
};

/** The most tests one Access order holds — `_MAX_TEST_CODES` in
 * `decoda/services/lab_adapters/access_csv.py`, which refuses anything past it. */
export const ACCESS_MAX_TESTS_PER_ORDER = 27;

/**
 * Access's own collection vocabulary, which is deliberately NOT Junction's:
 * both Access mobile options would collapse into `at_home_phlebotomy` and lose
 * the distinction the clinic is actually choosing between. Labels are the
 * compendium's own names, because Charles named two phlebotomist varieties
 * without saying what differs between them — a label asserting which is which
 * would be a guess.
 *
 * Shared by the ordering flow and the order detail so the two cannot drift.
 */
export const ACCESS_COLLECTION_METHOD_LABELS: Record<
  AccessCollectionMethod,
  string
> = {
  practice_draw: "On-site collection",
  walk_in_lab: "Walk-in lab",
  mobile_draw: "Mobile draw",
  local_mobile_draw: "Local mobile draw",
};

export const FASTING_STATUS_LABELS: Record<FastingStatus, string> = {
  yes: "Yes",
  no: "No",
  not_indicated: "Not Indicated",
};

/**
 * The collection methods staff can pick, offered for every order regardless of
 * lab or panel. Junction chooses collection method once per order and accepts
 * any method the lab supports, so narrowing this list by the lab's advertised
 * `collection_methods` (or by a panel's default `method`) only hid workable
 * options — most labs advertise a single method and staff were left with no
 * choice at all. `testkit` stays in `LabCollectionMethod` so historical orders
 * still render their label, but it is no longer offered.
 */
export const ALL_COLLECTION_METHODS: {
  value: LabCollectionMethod;
  label: string;
}[] = [
  { value: "walk_in_test", label: COLLECTION_METHOD_LABELS.walk_in_test },
  {
    value: "at_home_phlebotomy",
    label: COLLECTION_METHOD_LABELS.at_home_phlebotomy,
  },
  {
    value: "on_site_collection",
    label: COLLECTION_METHOD_LABELS.on_site_collection,
  },
];

/**
 * Every method the enum can hold, including retired ones, for filters and other
 * retrospective views over orders already placed.
 *
 * Deliberately not the same list as `ALL_COLLECTION_METHODS`: that one answers
 * "what may staff choose now" and so omits `testkit`, while a filter over
 * historical orders has to be able to reach a Test Kit order that is sitting
 * right there in the list. Reusing the picker's list here made such an order
 * visible but unfilterable.
 */
export const ALL_COLLECTION_METHODS_FOR_FILTERS: {
  value: LabCollectionMethod;
  label: string;
}[] = (Object.keys(COLLECTION_METHOD_LABELS) as LabCollectionMethod[]).map(
  (value) => ({ value, label: COLLECTION_METHOD_LABELS[value] }),
);

/**
 * Every billing type, in display order. The LABEL source, not the picker's
 * option list — the flow offers only `allowedBillingTypes`, but a past order can
 * still hold a type that is no longer offered.
 */
export const BILLING_TYPES: { value: LabBillingType; label: string }[] = [
  { value: "client_bill", label: "Client Bill" },
  { value: "commercial_insurance", label: "Commercial Insurance" },
  { value: "patient_bill", label: "Patient Bill" },
  { value: "patient_bill_passthrough", label: "Patient Bill (Passthrough)" },
];

/**
 * Junction refuses any order carrying ICD-10 codes under client or passthrough
 * billing ("ICD codes are not supported for client_bill" / "...for
 * patient_bill_passthrough") — no claim is filed, so there is nothing to code.
 * The codes are neither collected nor sent for those billing types.
 */
export function billingSupportsIcd(billingType?: LabBillingType): boolean {
  return (
    billingType !== "client_bill" && billingType !== "patient_bill_passthrough"
  );
}

/**
 * ICD-10 diagnosis codes are required for every billing type that carries
 * them: third-party and patient-bill orders.
 */
export function billingRequiresIcd(billingType?: LabBillingType): boolean {
  return billingType !== undefined && billingSupportsIcd(billingType);
}

/**
 * Flows where Junction's network supplies the ordering physician. Not the same
 * split as account provenance: `result_delegated` is clinic-brought, but
 * Junction still orders on it, so there is no clinic physician to bill against.
 */
export const JUNCTION_ORDERING_FLOWS = new Set([
  "not_delegated",
  "result_delegated",
]);

const NON_ALPHA = /[^a-z]/g;

function isBioreferenceLab(lab?: string | null): boolean {
  return (lab ?? "").toLowerCase().replace(NON_ALPHA, "") === "bioreference";
}

function normalizeState(state?: string | null): string {
  return (state ?? "").trim().toUpperCase();
}

/** Either the account is configured that way, or staff picked the network. */
export function ordersWithJunctionPhysicians(
  account: LabAccount | null | undefined,
  physicianMode?: string | null,
): boolean {
  if (physicianMode === "network") return true;
  return JUNCTION_ORDERING_FLOWS.has(account?.delegated_flow ?? "");
}

/**
 * The billing types Junction will accept for one account and patient.
 * `allowed_billing` is the per-account authority; Junction-ordered orders keep
 * only client bill and passthrough; passthrough is BioReference-only; client
 * bill is the floor. Mirrored by `decoda/services/lab_billing.py`.
 */
export function allowedBillingTypes({
  account,
  patientState,
  physicianMode,
}: {
  account: LabAccount | null | undefined;
  patientState?: string | null;
  physicianMode?: string | null;
}): LabBillingType[] {
  if (!account) return ["client_bill"];

  const allowedByType = account.allowed_billing ?? {};
  const state = normalizeState(patientState);
  const junctionOrders = ordersWithJunctionPhysicians(account, physicianMode);
  const bioreference = isBioreferenceLab(account.lab);

  const offered = BILLING_TYPES.map((b) => b.value).filter((type) => {
    const states = allowedByType[type] ?? [];
    if (!state || !states.some((s) => normalizeState(s) === state)) {
      return false;
    }
    if (
      junctionOrders &&
      type !== "client_bill" &&
      type !== "patient_bill_passthrough"
    ) {
      return false;
    }
    if (type === "patient_bill_passthrough" && !bioreference) return false;
    return true;
  });

  return offered.length > 0 ? offered : ["client_bill"];
}

/** A pick that is no longer orderable counts as no pick. */
export function resolveLabAccount(
  accounts: LabAccount[],
  labSlug: string,
  chosenId: string | null | undefined,
): LabAccount | null {
  const options = accountsForLab(accounts, labSlug);
  const chosen = chosenId ? options.find((a) => a.id === chosenId) : undefined;
  if (chosen) return chosen;
  return options.length === 1 ? options[0] : null;
}

export function resolveBucketAccount(
  accounts: LabAccount[],
  bucket: CartBucket,
): LabAccount | null {
  return resolveLabAccount(accounts, bucket.labSlug, bucket.labAccountId);
}

/**
 * Billing is picked once per order while accounts are per-bucket, so the offered
 * set is the intersection. One bucket today (`MULTI_LAB_ENABLED` is false).
 */
export function allowedBillingTypesForCart(
  buckets: CartBucket[],
  accounts: LabAccount[],
  patientState?: string | null,
  physicianMode?: string | null,
): LabBillingType[] {
  if (buckets.length === 0) return ["client_bill"];
  const shared = buckets
    .map((bucket) =>
      allowedBillingTypes({
        account: resolveBucketAccount(accounts, bucket),
        patientState,
        physicianMode,
      }),
    )
    .reduce((acc, types) => acc.filter((type) => types.includes(type)));
  return shared.length > 0 ? shared : ["client_bill"];
}

export function billingTypeLabel(type: LabBillingType): string {
  return BILLING_TYPES.find((b) => b.value === type)?.label ?? type;
}

function joinLabels(types: LabBillingType[]): string {
  const labels = types.map(billingTypeLabel);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * Why the picker offers fewer types than this account could reach, or null.
 * "Could reach" excludes passthrough off BioReference — its absence there is the
 * norm, and saying so on every order would be noise.
 */
export function billingRestrictionReason({
  account,
  accountOptionCount,
  physicianMode,
  allowed,
}: {
  account: LabAccount | null | undefined;
  /** Distinguishes "none picked yet" from "none configured", which read very
   *  differently: the latter has no picker to point staff at. */
  accountOptionCount?: number;
  physicianMode?: string | null;
  allowed: LabBillingType[];
}): string | null {
  const reachable = BILLING_TYPES.filter(
    (b) =>
      b.value !== "patient_bill_passthrough" || isBioreferenceLab(account?.lab),
  );
  if (allowed.length >= reachable.length) return null;
  const list = joinLabels(allowed);
  const verb = allowed.length === 1 ? "is" : "are";
  if (!account) {
    return accountOptionCount === 0
      ? `This lab has no billing account configured, so only ${list} ${verb} available.`
      : `Only ${list} ${verb} available until a lab account is chosen for every order.`;
  }
  if (ordersWithJunctionPhysicians(account, physicianMode)) {
    // Blaming the account for a network chosen two fields below reads as a
    // misconfiguration of an account that is configured correctly.
    return JUNCTION_ORDERING_FLOWS.has(account.delegated_flow ?? "")
      ? `Junction's physicians order on this account, so only ${list} ${verb} available.`
      : `The Junction physician network is ordering, so only ${list} ${verb} available.`;
  }
  return `This lab account supports only ${list} in the patient's state.`;
}

export function isClinicLabAccount(account: LabAccount): boolean {
  return account.delegated_flow !== "not_delegated";
}

export function labPricesHidden(
  accounts: LabAccount[] | null | undefined,
  labSlug: string | null | undefined,
): boolean {
  // Unknown accounts hide prices, so they never flash on before the answer.
  if (!Array.isArray(accounts)) return true;
  const options = accountsForLab(accounts, labSlug ?? "");
  return options.length > 0 && options.every(isClinicLabAccount);
}

/**
 * The two lines the account picker shows. A null `account_name` used to render a
 * raw UUID where picking wrong changes who gets billed.
 */
export function formatLabAccount(account: LabAccount): {
  primary: string;
  secondary?: string;
} {
  const primary =
    account.account_name?.trim() ||
    (isClinicLabAccount(account) ? "Your account" : "Junction account");
  return { primary, secondary: account.provider_account_id || undefined };
}

/** Shared (order-wide) details collected once and applied to every order. */
export interface SharedOrderDetails {
  billingType: LabBillingType;
  icdCodes: ICDTenCode[];
  clinicalNotes: string;
  // UI-only: which provider is picked in the "My provider" dropdown. The
  // payload carries the name/NPI fields below, populated from the selection.
  physicianProviderId: string;
  physicianFirstName: string;
  physicianLastName: string;
  physicianNpi: string;
  priority: boolean;
  insuranceCoverageId: string;
  junctionPayorCode: string | null;
  junctionPayorName: string;
  // The parent or guardian Junction requires for a patient under 18. Null for
  // an adult, and in raw state until staff edit the prefilled one.
  medicalProxy: LabMedicalProxy | null;
}

/** One AOE question plus every marker in the cart that requires it. */
export interface AoEQuestionAggregate {
  question: AoEQuestion;
  markerIds: number[];
}

export function insuranceValid(details: SharedOrderDetails): boolean {
  if (details.billingType !== "commercial_insurance") return true;
  return Boolean(details.insuranceCoverageId && details.junctionPayorCode);
}

/**
 * The medical proxy a minor's order starts from: a blank parent or guardian at
 * the patient's own address, since a minor usually lives with them.
 */
export function defaultMedicalProxy(patient: {
  address?: string;
  addressLineTwo?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): LabMedicalProxy {
  return {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    address: patient.address ?? "",
    addressLineTwo: patient.addressLineTwo ?? "",
    city: patient.city ?? "",
    state: patient.state ?? "",
    zipCode: patient.zipCode ?? "",
  };
}

/** Everything Junction requires of a medical proxy is filled in. */
export function medicalProxyComplete(proxy: LabMedicalProxy | null): boolean {
  if (!proxy) return false;
  const required = [
    proxy.firstName,
    proxy.lastName,
    proxy.address,
    proxy.city,
    proxy.state,
    proxy.zipCode,
  ];
  return (
    required.every((value) => value.trim().length > 0) &&
    proxy.phoneNumber.replace(/\D/g, "").length >= 10
  );
}

export function policyholderMatchesPatient(
  coverage: Pick<InsuranceCoverage, "policyHolderName" | "policyHolderDob">,
  patient: { firstName?: string; lastName?: string; dateOfBirth?: string },
): boolean {
  const patientName = `${patient.firstName ?? ""} ${patient.lastName ?? ""}`
    .trim()
    .toLowerCase();
  const coverageName = (coverage.policyHolderName ?? "").trim().toLowerCase();
  const patientDob = (patient.dateOfBirth ?? "").slice(0, 10);
  const coverageDob = (coverage.policyHolderDob ?? "").slice(0, 10);
  return (
    patientName.length > 0 &&
    patientName === coverageName &&
    patientDob.length > 0 &&
    patientDob === coverageDob
  );
}

export function formatJunctionPayorAddress(
  address: JunctionPayor["orgAddress"],
): string {
  if (!address) return "";
  return [
    address.firstLine,
    address.city,
    [address.state, address.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
}

export interface EffectiveJunctionPayor {
  code: string;
  name: string;
  fromLastOrder: boolean;
}

export function effectiveJunctionPayor(
  details: Pick<SharedOrderDetails, "junctionPayorCode" | "junctionPayorName">,
  lastPayor: { payorCode: string; payorName: string } | null | undefined,
): EffectiveJunctionPayor {
  if (details.junctionPayorCode === null) {
    return { code: "", name: "", fromLastOrder: false };
  }
  if (details.junctionPayorCode) {
    return {
      code: details.junctionPayorCode,
      name: details.junctionPayorName,
      fromLastOrder: false,
    };
  }
  if (lastPayor) {
    return {
      code: lastPayor.payorCode,
      name: lastPayor.payorName,
      fromLastOrder: true,
    };
  }
  return { code: "", name: "", fromLastOrder: false };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function formatTat(
  common?: number | null,
  worst?: number | null,
): string {
  const days = (n: number) => `${n} day${n !== 1 ? "s" : ""}`;
  if (common && worst && worst > common)
    return `Results in ${common}–${worst} days`;
  if (common) return `Results in ${days(common)}`;
  if (worst) return `Results in up to ${days(worst)}`;
  return "";
}

export function getLabName(
  labMap: Map<number, LabInfo>,
  labId: number,
): string {
  return labMap.get(labId)?.name ?? `Lab ${labId}`;
}

// Junction lab slugs. Quest leads the lab pickers while Labcorp is the lab
// they open on.
const TOP_LAB_SLUG = "quest";
const DEFAULT_LAB_SLUG = "labcorp";

/** Labs in picker order: Quest first, the rest in Junction's order. */
export function sortLabsForPicker(labs: LabInfo[]): LabInfo[] {
  return [
    ...labs.filter((lab) => lab.slug === TOP_LAB_SLUG),
    ...labs.filter((lab) => lab.slug !== TOP_LAB_SLUG),
  ];
}

/** The lab a picker preselects: Labcorp when offered, else the first lab. */
export function defaultPickerLab(labs: LabInfo[]): LabInfo | undefined {
  return labs.find((lab) => lab.slug === DEFAULT_LAB_SLUG) ?? labs[0];
}

const PANEL_STATUSES: readonly LabTestStatus[] = [
  "active",
  "pending_approval",
  "inactive",
];

/**
 * Junction's catalog does not filter by status, and `status` is a compile-time union
 * only — the catalog is proxied verbatim. A status we do not recognise reads as active
 * so a value added on their side cannot silently empty the wizard.
 */
export function panelStatus(test: LabTest): LabTestStatus {
  if (test.status) {
    return PANEL_STATUSES.includes(test.status) ? test.status : "active";
  }
  return test.is_active === false ? "inactive" : "active";
}

export const isOrderablePanel = (test: LabTest): boolean =>
  panelStatus(test) === "active";

/** A panel's lab test code. Junction gives codes per marker (`provider_id`),
 *  not per panel, so only a one-marker panel has one to show. */
export const panelTestCode = (test: LabTest): string | undefined =>
  test.markers?.length === 1 ? test.markers[0].provider_id : undefined;

// ---------------------------------------------------------------------------
// Lab anchoring
// ---------------------------------------------------------------------------

/** Derive the anchor state from a single bucket. */
export function deriveAnchorState(bucket: CartBucket): AnchorState {
  const hasPresets = bucket.presets.length > 0;
  const hasMarkers = bucket.markers.length > 0;

  if (!hasPresets && !hasMarkers) return "agnostic";

  // Preset always hard-anchors to its lab
  if (hasPresets) return "hard";

  // All markers from one lab = hard anchor
  if (hasMarkers) {
    const labIds = new Set(bucket.markers.map((m) => m.lab_id));
    if (labIds.size === 1) return "hard";
    return "soft";
  }

  return "agnostic";
}

/** Get the single anchor lab from a bucket (only meaningful for a hard anchor). */
export function getAnchorLab(
  bucket: CartBucket,
  labMap: Map<number, LabInfo>,
): LabInfo | null {
  if (bucket.presets.length > 0 && bucket.presets[0].lab?.id) {
    return labMap.get(bucket.presets[0].lab.id) ?? null;
  }
  if (bucket.markers.length > 0) {
    return labMap.get(bucket.markers[0].lab_id) ?? null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Bucket construction
// ---------------------------------------------------------------------------

/**
 * Distribute markers and presets into one bucket per lab (one order per lab).
 * Sorted by item count desc so the lab carrying the bulk of the order is first.
 */
export function autoSortIntoBuckets(
  markers: Marker[],
  presets: LabTest[],
  labMap: Map<number, LabInfo>,
): CartBucket[] {
  // Group markers by lab
  const markersByLab = new Map<number, Marker[]>();
  for (const m of markers) {
    const arr = markersByLab.get(m.lab_id) ?? [];
    arr.push(m);
    markersByLab.set(m.lab_id, arr);
  }

  // Group presets by lab
  const presetsByLab = new Map<number, LabTest[]>();
  for (const p of presets) {
    if (p.lab?.id) {
      const arr = presetsByLab.get(p.lab.id) ?? [];
      arr.push(p);
      presetsByLab.set(p.lab.id, arr);
    }
  }

  // Collect all unique lab IDs
  const allLabIds = new Set([
    ...Array.from(markersByLab.keys()),
    ...Array.from(presetsByLab.keys()),
  ]);

  const buckets: CartBucket[] = [];
  for (const labId of Array.from(allLabIds)) {
    const lab = labMap.get(labId);
    buckets.push({
      labId,
      labName: lab?.name ?? `Lab ${labId}`,
      labSlug: lab?.slug ?? "",
      presets: presetsByLab.get(labId) ?? [],
      markers: markersByLab.get(labId) ?? [],
      collectionMethod: null,
      methodSource: null,
      labAccountId: null,
      psc: null,
    });
  }

  // Most items first (cost optimization: preferred lab gets the bulk)
  buckets.sort(
    (a, b) =>
      b.presets.length +
      b.markers.length -
      (a.presets.length + a.markers.length),
  );

  return buckets;
}

/** Create an empty bucket for a given lab. */
export function createBucketForLab(
  labId: number,
  labMap: Map<number, LabInfo>,
): CartBucket {
  const lab = labMap.get(labId);
  return {
    labId,
    labName: lab?.name ?? `Lab ${labId}`,
    labSlug: lab?.slug ?? "",
    presets: [],
    markers: [],
    collectionMethod: null,
    methodSource: null,
    labAccountId: null,
    psc: null,
  };
}

// ---------------------------------------------------------------------------
// Collection methods
// ---------------------------------------------------------------------------

/**
 * True when a method is one the picker still offers. A method can reach cart
 * state without passing through the picker — a reopened draft, or a panel's
 * Junction default — so every such entry point runs it through this guard.
 * Without it a retired method (`testkit`) would sit in state unrendered by the
 * radio group yet still satisfy the "every order has a method" placement gate.
 */
export function isOfferedCollectionMethod(
  method?: string | null,
): method is LabCollectionMethod {
  return ALL_COLLECTION_METHODS.some((m) => m.value === method);
}

/**
 * The panels' shared default method, used to pre-select the radio.
 *
 * A panel with no `method` abstains rather than disagrees — Junction simply has
 * no default for it — so it doesn't veto the one opinion the others express.
 * Only panels that name *different* methods, or a lone default the picker no
 * longer offers, yield null and leave staff to choose. The result is always a
 * suggestion: the bucket records it as `methodSource: "preset"`, so it is
 * recomputed when the panel set changes and never outranks an explicit pick.
 */
export function sharedPresetMethod(
  presets: LabTest[],
): LabCollectionMethod | null {
  const methods = new Set(
    presets.map((p) => p.method).filter((m): m is string => !!m),
  );
  if (methods.size !== 1) return null;
  const [method] = Array.from(methods);
  return isOfferedCollectionMethod(method) ? method : null;
}

const ORDERABLE_LAB_ACCOUNT_STATUSES = new Set(["active", "ready_to_launch"]);

/**
 * The orderable (active or ready-to-launch) Junction accounts that can bill
 * this lab, matched by lab slug (account.lab is a Junction lab enum, e.g.
 * "labcorp"/"quest"). One → auto-select; several → staff pick; none → let
 * Junction resolve (send nothing).
 */
export function accountsForLab(
  accounts: LabAccount[],
  labSlug: string,
): LabAccount[] {
  return accounts.filter(
    (a) => ORDERABLE_LAB_ACCOUNT_STATUSES.has(a.status) && a.lab === labSlug,
  );
}

export function accountsForBucket(
  accounts: LabAccount[],
  bucket: CartBucket,
): LabAccount[] {
  return accountsForLab(accounts, bucket.labSlug);
}

// ---------------------------------------------------------------------------
// AOE (Ask-On-Entry) aggregation
// ---------------------------------------------------------------------------

/**
 * Every marker that contributes to a bucket's order: à-la-carte selections plus
 * the loaded contents of each selected panel. AOE applies to panel markers just
 * like à-la-carte ones, so this is the single source for asking/validating/
 * sending answers. Deduped by marker id. (Panel contents only appear here once
 * their markers have loaded — submission is gated on `arePresetMarkersReady`.)
 */
export function getBucketAoeMarkers(
  bucket: CartBucket,
  presetMarkerMap: Map<string, Marker[]>,
): Marker[] {
  const byId = new Map<number, Marker>();
  for (const m of bucket.markers) byId.set(m.id, m);
  for (const preset of bucket.presets) {
    for (const m of presetMarkerMap.get(preset.id) ?? []) {
      if (!byId.has(m.id)) byId.set(m.id, m);
    }
  }
  return Array.from(byId.values());
}

/**
 * Collect unique AOE questions across all buckets (à-la-carte + panel markers),
 * deduped by question ID, tracking which markers need each question for payload
 * mapping. Sorted by the question's display sequence.
 */
export function aggregateAoeQuestions(
  buckets: CartBucket[],
  presetMarkerMap: Map<string, Marker[]>,
): AoEQuestionAggregate[] {
  const questionMap = new Map<number, AoEQuestionAggregate>();
  for (const bucket of buckets) {
    for (const marker of getBucketAoeMarkers(bucket, presetMarkerMap)) {
      for (const q of marker.aoe?.questions ?? []) {
        const existing = questionMap.get(q.id);
        if (existing) {
          if (!existing.markerIds.includes(marker.id)) {
            existing.markerIds.push(marker.id);
          }
        } else {
          questionMap.set(q.id, { question: q, markerIds: [marker.id] });
        }
      }
    }
  }
  return Array.from(questionMap.values()).sort(
    (a, b) => a.question.sequence - b.question.sequence,
  );
}

/** The AOE questions one bucket asks (à-la-carte + panel markers), sorted. */
export function getBucketAoeQuestions(
  bucket: CartBucket,
  presetMarkerMap: Map<string, Marker[]>,
): AoEQuestion[] {
  const byId = new Map<number, AoEQuestion>();
  for (const marker of getBucketAoeMarkers(bucket, presetMarkerMap)) {
    for (const q of marker.aoe?.questions ?? []) {
      if (!byId.has(q.id)) byId.set(q.id, q);
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.sequence - b.sequence);
}

/**
 * The answer option a choice response picks. Responses are the option's code;
 * drafts saved before that hold its label, matched here ignoring case.
 */
export function findAoeOption(
  question: AoEQuestion,
  response: string,
): AoEAnswerOption | undefined {
  const options = question.answers ?? [];
  const label = response.trim().toLowerCase();
  return (
    options.find((o) => o.code === response) ??
    options.find((o) => o.value.trim().toLowerCase() === label)
  );
}

/** Build per-marker aoe_answers from the deduplicated responses for one bucket. */
export function buildAoeAnswersForBucket(
  bucket: CartBucket,
  presetMarkerMap: Map<string, Marker[]>,
  aoeResponses: Record<number, string | string[]>,
): AoEAnswer[] {
  const answers: AoEAnswer[] = [];
  for (const marker of getBucketAoeMarkers(bucket, presetMarkerMap)) {
    for (const q of marker.aoe?.questions ?? []) {
      const response = aoeResponses[q.id];
      // multi_choice answers are a list — emit one AoEAnswer per selected value.
      const values = Array.isArray(response)
        ? response
        : response !== undefined && response !== ""
          ? [response]
          : [];
      for (const value of values) {
        answers.push({
          markerId: marker.id,
          questionId: q.id,
          answer: findAoeOption(q, value)?.code ?? value,
        });
      }
    }
  }
  return answers;
}

/** True when any required, aggregated AOE question is still unanswered. */
export function hasUnansweredRequiredAoe(
  aggregation: AoEQuestionAggregate[],
  aoeResponses: Record<number, string | string[]>,
): boolean {
  return aggregation.some(({ question }) => {
    if (!question.required) return false;
    const response = aoeResponses[question.id];
    return Array.isArray(response) ? response.length === 0 : !response;
  });
}

// ---------------------------------------------------------------------------
// Dedup
// ---------------------------------------------------------------------------

/** All marker IDs included in any selected preset (across all buckets). */
export function collectPresetMarkerIds(
  presetMarkerMap: Map<string, Marker[]>,
): Set<number> {
  const ids = new Set<number>();
  Array.from(presetMarkerMap.values()).forEach((markers) => {
    markers.forEach((m) => ids.add(m.id));
  });
  return ids;
}

/** All marker IDs in the cart: à-la-carte selections + every preset's contents. */
export function collectSelectedMarkerIds(
  buckets: CartBucket[],
  presetMarkerIds: Set<number>,
): Set<number> {
  const ids = new Set<number>();
  for (const bucket of buckets) {
    for (const m of bucket.markers) ids.add(m.id);
  }
  Array.from(presetMarkerIds).forEach((id) => ids.add(id));
  return ids;
}

/**
 * Selected preset ids whose marker contents aren't in the map yet — still
 * loading, errored, or never fetched. `buildOrderPayload` needs these to dedup
 * à-la-carte markers a panel already covers, so submission must stay
 * blocked while any preset is unready (data-presence, not query lifecycle, so a
 * failed query counts as unready too).
 */
export function getUnreadyPresetIds(
  buckets: CartBucket[],
  presetMarkerMap: Map<string, Marker[]>,
): string[] {
  const selected = new Set(buckets.flatMap((b) => b.presets.map((p) => p.id)));
  return Array.from(selected).filter((id) => !presetMarkerMap.has(id));
}

/** True when every selected preset's marker contents are known. */
export function arePresetMarkersReady(
  buckets: CartBucket[],
  presetMarkerMap: Map<string, Marker[]>,
): boolean {
  return getUnreadyPresetIds(buckets, presetMarkerMap).length === 0;
}

// ---------------------------------------------------------------------------
// Draft payload
// ---------------------------------------------------------------------------

/**
 * Build the `LabOrderCreate` payload for one bucket.
 *
 * Presets go whole: as add-on markers, one that isn't à-la-carte fails the order.
 */
export function buildOrderPayload(
  bucket: CartBucket,
  details: SharedOrderDetails,
  presetMarkerMap: Map<string, Marker[]>,
  aoeResponses: Record<number, string | string[]>,
  // "own" | "network" — persisted so reopening a draft restores the exact
  // ordering-physician mode the user chose (an empty-physician draft saved in
  // "own" mode must not silently rehydrate as always-valid "network").
  physicianMode?: string,
): LabOrderCreate {
  // Codes are carried even for billing types the vendor won't accept them for:
  // this payload is also the draft PATCH body, and the server rebuilds
  // local_metadata from it wholesale, so dropping them here would erase them
  // from the saved draft. Junction never sees them — submit filters the vendor
  // payload instead.
  const parsedIcdCodes = details.icdCodes.map((c) => c.code);

  // À-la-carte markers already covered by one of the bucket's selected presets
  // must not be re-sent as add-ons (would double-bill). Dedup at the payload
  // boundary too, in case a panel was selected before its contents loaded and
  // the selection-time dedup couldn't catch it.
  const presetCoveredMarkerIds = new Set<number>();
  for (const preset of bucket.presets) {
    for (const m of presetMarkerMap.get(preset.id) ?? []) {
      presetCoveredMarkerIds.add(m.id);
    }
  }
  const addonMarkers = bucket.markers.filter(
    (m) => !presetCoveredMarkerIds.has(m.id),
  );

  const labTestIds =
    bucket.presets.length > 0 ? bucket.presets.map((p) => p.id) : undefined;
  const markerIds =
    addonMarkers.length > 0 ? addonMarkers.map((m) => m.id) : undefined;
  const items: LabOrderCreateItem[] = [
    ...bucket.presets.map(
      (preset): LabOrderCreateItem => ({
        itemType: "lab_test",
        vendorCatalogId: preset.id,
        displayName: preset.name,
        isStripped: false,
      }),
    ),
    ...addonMarkers.map(
      (marker): LabOrderCreateItem => ({
        itemType: "marker",
        vendorCatalogId: String(marker.id),
        displayName: marker.name,
        isStripped: false,
      }),
    ),
  ];

  const bucketAoeAnswers = buildAoeAnswersForBucket(
    bucket,
    presetMarkerMap,
    aoeResponses,
  );
  const pscAddress = bucket.psc
    ? [
        bucket.psc.metadata.first_line,
        bucket.psc.metadata.city,
        [bucket.psc.metadata.state, bucket.psc.metadata.zip_code]
          .filter(Boolean)
          .join(" "),
      ]
        .filter(Boolean)
        .join(", ")
    : undefined;

  return {
    labTestIds,
    markerIds,
    collectionMethod: bucket.collectionMethod ?? undefined,
    billingType: details.billingType,
    icdCodes: parsedIcdCodes.length > 0 ? parsedIcdCodes : undefined,
    clinicalNotes: details.clinicalNotes || undefined,
    physicianFirstName: details.physicianFirstName || undefined,
    physicianLastName: details.physicianLastName || undefined,
    physicianNpi: details.physicianNpi || undefined,
    physicianId: details.physicianProviderId || undefined,
    physicianMode: physicianMode || undefined,
    priority: details.priority,
    insuranceCoverageId: details.insuranceCoverageId || undefined,
    junctionPayorCode: details.junctionPayorCode || null,
    junctionPayorName: details.junctionPayorName || null,
    medicalProxy: details.medicalProxy,
    pscName: bucket.psc?.metadata.name,
    pscAddress,
    labSlug: bucket.labSlug || undefined,
    labName: bucket.labName || undefined,
    labAccountId: bucket.labAccountId ?? undefined,
    aoeAnswers: bucketAoeAnswers.length > 0 ? bucketAoeAnswers : undefined,
    items: items.length > 0 ? items : undefined,
  };
}
